import { Link } from 'react-router-dom';
import { ArrowUpRight, ShieldCheck, Clock } from 'lucide-react';
import { Reveal } from '../components/Reveal';
import { TrustBadges } from '../components/TrustBadges';
import { ServiceCard } from '../components/ServiceCard';
import { Testimonials } from '../components/Testimonials';
import { FAQSection } from '../components/FAQSection';
import { Watermark } from '../components/Watermark';
import { COMPANY, SERVICES, STATS, HERO_IMAGE, REAL_BEFORE_AFTER } from '../lib/site';

const HomePage = ({ onOpenQuote }) => {
    return (
        <main data-testid="home-page">
            {/* HERO */}
            <section className="relative pt-12 md:pt-20 pb-20 md:pb-32 overflow-hidden">
                <div className="container-axion grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    <div className="lg:col-span-6 relative z-10">
                        <Reveal>
                            <div className="inline-flex items-center bg-muted border border-border rounded-full px-4 py-1.5 mb-7">
                                <span className="text-[11px] font-outfit tracking-wider uppercase text-foreground">
                                    Bristol&apos;s quietly thorough cleaning team
                                </span>
                            </div>
                        </Reveal>

                        <Reveal delay={80}>
                            <h1 data-testid="hero-headline" className="font-outfit font-medium tracking-tight text-foreground leading-[1.02] text-5xl sm:text-6xl lg:text-[5.4rem]">
                                Cleaning you can<br />
                                <em className="italic font-light text-accent">rely on.</em>
                            </h1>
                        </Reveal>

                        <Reveal delay={160}>
                            <p className="mt-7 max-w-lg text-muted-foreground text-lg leading-relaxed">
                                Premium domestic, commercial, end-of-tenancy and Airbnb cleaning, delivered by a team of professional cleaners to
                                exceptional standards every time.
                            </p>
                        </Reveal>

                        <Reveal delay={240}>
                            <div className="mt-9 flex flex-wrap gap-3">
                                <button
                                    data-testid="hero-quote-button"
                                    onClick={onOpenQuote}
                                    className="btn-primary-pill"
                                >
                                    Get a Free Quote
                                    <ArrowUpRight className="w-4 h-4" strokeWidth={1.8} />
                                </button>
                                <a
                                    data-testid="hero-whatsapp-button"
                                    href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent(COMPANY.whatsappMessage)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-secondary-pill"
                                >
                                    WhatsApp us
                                </a>
                            </div>
                        </Reveal>

                        <Reveal delay={320}>
                            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6">
                                {STATS.map((s) => (
                                    <div key={s.label}>
                                        <div className="font-outfit text-2xl md:text-3xl tracking-tight text-foreground">{s.value}</div>
                                        <div className="text-xs text-muted-foreground mt-1">{s.label}</div>
                                    </div>
                                ))}
                            </div>
                        </Reveal>
                    </div>

                    <div className="lg:col-span-6 relative">
                        <Reveal delay={120}>
                            <div className="relative">
                                <div className="absolute -inset-4 md:-inset-8 bg-accent/20 rounded-[2.5rem] -z-10" />
                                <div className="w-full aspect-[4/5] md:aspect-[5/6] overflow-hidden rounded-2xl shadow-[0_30px_80px_-30px_rgba(47,130,105,0.4)] relative">
                                    <img
                                        src={HERO_IMAGE}
                                        alt="Professional AXION cleaners in branded polos delivering domestic, residential, bathroom and office cleaning"
                                        className="w-full h-full object-cover"
                                        style={{ transform: 'scale(1.32)', transformOrigin: 'center 22%' }}
                                    />
                                    <Watermark size="md" position="br" />
                                </div>
                                <div className="absolute -bottom-6 -left-6 md:-left-10 bg-white border border-border rounded-2xl p-5 max-w-[230px] shadow-[0_18px_50px_-12px_rgba(17,47,34,0.3)]">
                                    <div className="flex items-center gap-2">
                                        <span className="grid place-items-center w-8 h-8 rounded-full bg-primary text-primary-foreground">
                                            <ShieldCheck className="w-4 h-4" strokeWidth={1.6} />
                                        </span>
                                        <div className="font-outfit text-sm">Deposit guarantee</div>
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                                        Free re-clean within 48 hours if anything is missed on end-of-tenancy.
                                    </p>
                                </div>
                                <div className="absolute -top-6 -right-3 md:-right-8 bg-primary text-primary-foreground rounded-2xl p-5 max-w-[210px]">
                                    <div className="flex items-center gap-2">
                                        <Clock className="w-4 h-4 text-accent" strokeWidth={1.6} />
                                        <div className="font-outfit text-sm">Reply &lt; 1 hour</div>
                                    </div>
                                    <p className="text-xs text-white/70 mt-2 leading-relaxed">
                                        Mon–Sat, 7:00–19:00.
                                    </p>
                                </div>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* TRUST */}
            <section className="border-y border-border bg-white">
                <div className="container-axion py-10">
                    <TrustBadges />
                </div>
            </section>

            {/* SERVICES */}
            <section id="services" className="py-24 md:py-32">
                <div className="container-axion">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14 items-end">
                        <Reveal className="md:col-span-7">
                            <div className="overline">Services</div>
                            <h2 className="text-4xl md:text-5xl lg:text-6xl font-outfit font-medium tracking-tight mt-4 leading-[1.05]">
                                Five disciplines.<br />
                                <em className="italic font-light text-accent">One standard.</em>
                            </h2>
                        </Reveal>
                        <Reveal delay={120} className="md:col-span-5">
                            <p className="text-muted-foreground text-[15px] leading-relaxed">
                                From weekly home care to hotel-grade Airbnb turnarounds — every visit is checklist-led,
                                photo-reported and backed by our 24-hour guarantee.
                            </p>
                            <Link
                                to="/services"
                                className="mt-4 inline-flex items-center gap-2 text-sm font-outfit text-foreground border-b border-foreground/40 hover:border-foreground pb-0.5 transition-colors"
                            >
                                Explore all services
                                <ArrowUpRight className="w-3.5 h-3.5" />
                            </Link>
                        </Reveal>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {SERVICES.map((s, i) => (
                            <Reveal key={s.slug} delay={i * 90}>
                                <ServiceCard service={s} onQuote={onOpenQuote} />
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* REAL BEFORE / AFTER — actual jobs */}
            <section data-testid="real-before-after" className="py-24 md:py-32 bg-muted">
                <div className="container-axion">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14 items-end">
                        <Reveal className="md:col-span-7">
                            <div className="overline">Real results</div>
                            <h2 className="text-4xl md:text-5xl lg:text-6xl font-outfit font-medium tracking-tight mt-4 leading-[1.05]">
                                Before. After.<br />
                                <em className="italic font-light text-accent">Real Bristol jobs.</em>
                            </h2>
                        </Reveal>
                        <Reveal delay={120} className="md:col-span-5">
                            <p className="text-muted-foreground text-[15px] leading-relaxed">
                                No staging. No filters. Real photos from our team, taken straight after the job —
                                limescale gone, grime lifted, fixtures restored.
                            </p>
                        </Reveal>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {REAL_BEFORE_AFTER.map((p, i) => (
                            <Reveal key={p.image} delay={i * 90}>
                                <figure className="group bg-white border border-border rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-[0_24px_60px_-30px_rgba(47,130,105,0.35)] transition-all duration-500">
                                    <div className="relative w-full aspect-[5/4] overflow-hidden bg-muted">
                                        <img
                                            src={p.image}
                                            alt={p.caption}
                                            loading="lazy"
                                            className="absolute inset-0 w-full h-full object-contain transition-transform duration-700 group-hover:scale-[1.03]"
                                        />
                                        <Watermark size="sm" position="br" />
                                    </div>
                                    <figcaption className="p-5">
                                        <div className="font-outfit text-base text-foreground">{p.caption}</div>
                                    </figcaption>
                                </figure>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <Testimonials />
            <FAQSection />

            {/* CTA */}
            <section className="py-24 md:py-32 bg-primary text-primary-foreground relative overflow-hidden">
                <div className="container-axion relative z-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                    <Reveal className="md:col-span-7">
                        <div className="overline text-accent">Ready when you are</div>
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-outfit font-medium tracking-tight mt-4 leading-[1.05]">
                            Tell us about<br />
                            <em className="italic font-light text-accent">your space.</em>
                        </h2>
                        <p className="text-white/70 mt-5 max-w-xl text-[15px] leading-relaxed">
                            A free, no-obligation quote — usually back in your inbox within the hour.
                        </p>
                    </Reveal>
                    <Reveal delay={120} className="md:col-span-5 flex flex-col sm:flex-row md:flex-col gap-3 md:items-end">
                        <button
                            onClick={onOpenQuote}
                            data-testid="cta-quote-button"
                            className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-primary px-7 py-4 text-sm font-medium font-outfit hover:-translate-y-0.5 transition-transform"
                        >
                            Get a Free Quote
                            <ArrowUpRight className="w-4 h-4" strokeWidth={1.8} />
                        </button>
                        <a
                            href={`mailto:${COMPANY.email}`}
                            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 text-white px-7 py-4 text-sm font-medium font-outfit hover:bg-white/10 transition-colors"
                        >
                            {COMPANY.email}
                        </a>
                    </Reveal>
                </div>
            </section>
        </main>
    );
};

export default HomePage;
